import { setIn } from 'timm';
import { call, delay, type SagaGenerator } from 'typed-redux-saga';
import { v4 as uuid } from 'uuid';
import {
  type AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  setupSlice,
} from '@owl-frontend/redux';
import * as activitiesApi from '../../../api/activities/client';
import type { Activity } from '../../../api/activities/interface';
import * as filesApi from '../../../api/files/client';
import { type ClaimFile, TEXT_MIME_TYPES } from '../../../api/files/interface';
import type { Notification } from '../../../api/notifications/interface';
import * as templatesApi from '../../../api/templates/client';
import type { ClaimSensor } from './interface';

export interface ClaimOverviewShellState {
  claimSummaries: {
    [dossierId: string]: string;
  };
  claimSensors: {
    [dossierId: string]: ClaimSensor[];
  };
  claimNotifications: {
    [dossierId: string]: Notification[];
  };
  claimTemplateSchemas: string[];
  claimFiles: {
    [dossierId: string]: {
      [fileId: string]: ClaimFile;
    };
  };
  claimFilePaths: {
    [fileId: string]: {
      [pageNumber: number]: {
        url: string;
        requestedDate: number;
      };
    };
  };
  claimActivities: {
    [dossierId: string]: {
      [activityId: string]: Activity;
    };
  };
  actions: {
    fetchClaimSummary: AsyncActionState<string>;
    fetchClaimSensors: AsyncActionState<ClaimSensor[]>;
    fetchClaimNotifications: AsyncActionState<Notification[]>;
    fetchTemplateSchemas: AsyncActionState<string[]>;
    fetchClaimFiles: AsyncActionState<ClaimFile[]>;
    fetchClaimFile: AsyncActionState<ClaimFile>;
    fetchClaimFilePaths: AsyncActionState<{ page: number; url: string }[]>;
    fetchClaimActivity: AsyncActionState<Activity>;
  };
}

const initialState: ClaimOverviewShellState = {
  claimSummaries: {},
  claimSensors: {},
  claimNotifications: {},
  claimTemplateSchemas: [],
  claimFiles: {},
  claimFilePaths: {},
  claimActivities: {},
  actions: {
    fetchClaimSummary: { status: 'uninitialized' },
    fetchClaimSensors: { status: 'uninitialized' },
    fetchClaimNotifications: { status: 'uninitialized' },
    fetchTemplateSchemas: { status: 'uninitialized' },
    fetchClaimFiles: { status: 'uninitialized' },
    fetchClaimFile: { status: 'uninitialized' },
    fetchClaimFilePaths: { status: 'uninitialized' },
    fetchClaimActivity: { status: 'uninitialized' },
  },
};

function* fetchClaimSummary(dummyAction: {
  payload: { dossierId: string };
}): SagaGenerator<string> {
  // TODO: implement me
  yield* delay(1_500);
  return 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.';
}

function* fetchClaimSensors(action: {
  payload: { dossierId: string };
}): SagaGenerator<ClaimSensor[]> {
  yield* delay(2_000);
  // TODO: implement me
  return [
    {
      claim_sensor_id: uuid(),
      dossier_id: action.payload.dossierId,
      status: 'done',
      description:
        'Medical documents are required for a complete claim analysis.',
      action_title: 'PTO Report',
      action_status: 'Not received',
      claim_sensor_items: [
        {
          status: 'done',
          title: 'Primary Provider Analysis',
          date: '2025-10-03',
        },
        {
          status: 'done',
          title: 'Surgical Consult & Results',
          date: '2025-10-11',
        },
      ],
      created_at: '2025-11-06',
      updated_at: '2025-11-06',
    },
    {
      claim_sensor_id: uuid(),
      dossier_id: action.payload.dossierId,
      status: 'pending',
      title: 'Pre-Ex Worksheet Completed',
      action_title: 'Pre-Ex Worksheet',
      action_status: 'Not generated',
      claim_sensor_items: [],
      created_at: '2025-11-06',
      updated_at: '2025-11-06',
    },
    {
      claim_sensor_id: uuid(),
      dossier_id: action.payload.dossierId,
      status: 'pending',
      title: 'Claim Decision Reached',
      action_title: 'Claim Decision',
      action_status: 'Not reached',
      claim_sensor_items: [],
      created_at: '2025-11-06',
      updated_at: '2025-11-06',
    },
  ];
}

function* fetchClaimNotifications(action: {
  payload: { dossierId: string };
}): SagaGenerator<Notification[]> {
  yield* delay(3_000);
  // TODO: implement me
  return [
    {
      notification_id: uuid(),
      dossier_id: action.payload.dossierId,
      notification_type: 'template_ready',
      notification_content: 'A new template was generated',
      notification_content_path:
        'claim_overview.templates.generate.successSpecific',
      notification_content_values: {
        template_type: 'Summaries',
      },
      notification_link_to: '',
      is_read: false,
      is_dismissed: false,
      created_at: '2025-10-21',
      updated_at: '2025-10-21',
    },
    {
      notification_id: uuid(),
      dossier_id: action.payload.dossierId,
      notification_type: 'Summaries',
      notification_content: 'New documents were received',
      notification_link_to: '',
      is_read: false,
      is_dismissed: false,
      created_at: '2025-10-21',
      updated_at: '2025-10-21',
    },
    {
      notification_id: uuid(),
      dossier_id: action.payload.dossierId,
      notification_type: 'Summaries',
      notification_content: 'A new note was added by Hugo Reyes',
      notification_link_to: '',
      is_read: false,
      is_dismissed: false,
      created_at: '2025-10-21',
      updated_at: '2025-10-21',
    },
  ];
}

type FileViewerType = 'PDF_FROM_TEXT' | 'PDF';

export const fetchFileViewerType = (claimFile: ClaimFile): FileViewerType => {
  if (TEXT_MIME_TYPES.includes(claimFile.mime_type ?? '')) {
    return 'PDF_FROM_TEXT';
  }

  return 'PDF';
};

export const FIRST_PAGE = 1;
export const PDF_RANGE = 5;

type FetchClaimFilePathsArg =
  | {
      type: 'PDF_FROM_TEXT';
      dossierId: string;
      fileId: string;
    }
  | {
      type: 'PDF';
      dossierId: string;
      fileId: string;
      pages: [start: number, end: number];
    }
  | { type?: never; dossierId: string; fileId: string };

function* fetchClaimFilePaths(action: {
  payload: FetchClaimFilePathsArg;
}): SagaGenerator<
  {
    page: number;
    url: string;
  }[]
> {
  const { type, dossierId, fileId } = action.payload;

  if (type === 'PDF_FROM_TEXT') {
    const { url } = yield* call(filesApi.fetchFileSource, {
      dossierId,
      fileId,
    });
    return [{ page: FIRST_PAGE, url }];
  }

  if (type === 'PDF') {
    const response = yield* call(filesApi.fetchFilePage, {
      dossierId,
      fileId,
      startPage: action.payload.pages[0],
      endPage: action.payload.pages[1],
    });

    return response.pages.map(({ page, url }) => ({
      page,
      url,
    }));
  }

  throw new Error('Invalid file type');
}

const slice = setupSlice('claim-overview-shell', initialState)
  .addAsyncSagas({
    fetchClaimSummary,
    fetchClaimSensors,
    fetchClaimNotifications,
    fetchTemplateSchemas: invoke(templatesApi.fetchTemplateSchemas),
    fetchClaimFiles: invoke(filesApi.fetchFiles),
    fetchClaimFile: invoke(filesApi.fetchFile),
    fetchClaimFilePaths,
    fetchClaimActivity: invoke(activitiesApi.fetchActivity),
  })
  .addReducers({
    'fetchClaimSummary/fulfilled': (state, action) => {
      state.claimSummaries = setIn(
        state.claimSummaries,
        [action.meta.arg.dossierId],
        action.payload
      ) as typeof state.claimSummaries;
    },
    'fetchClaimSensors/fulfilled': (state, action) => {
      state.claimSensors = setIn(
        state.claimSensors,
        [action.meta.arg.dossierId],
        action.payload
      ) as typeof state.claimSensors;
    },
    'fetchClaimNotifications/fulfilled': (state, action) => {
      state.claimNotifications = setIn(
        state.claimNotifications,
        [action.meta.arg.dossierId],
        action.payload
      ) as typeof state.claimNotifications;
    },
    'fetchTemplateSchemas/fulfilled': (state, action) => {
      state.claimTemplateSchemas = action.payload;
    },
    'fetchClaimFiles/fulfilled': (state, action) => {
      const dossier_id = action.meta.arg.dossier_id;
      const offset = action.meta.arg.offset;

      const newFiles = action.payload.files.reduce((acc, file) => {
        acc[file.file_id] = file;
        return acc;
      }, {} as Record<string, ClaimFile>);

      if (!offset) {
        state.claimFiles[dossier_id] = newFiles;
      } else {
        state.claimFiles[dossier_id] = {
          ...state.claimFiles[dossier_id],
          ...newFiles,
        };
      }
    },
    'fetchClaimFile/fulfilled': (state, action) => {
      const { dossier_id, file_id } = action.meta.arg;
      state.claimFiles = setIn(
        state.claimFiles,
        [dossier_id, file_id],
        action.payload
      ) as typeof state.claimFiles;
    },
    'fetchClaimFilePaths/fulfilled': (state, action) => {
      const { fileId } = action.meta.arg;
      for (const payload of action.payload) {
        const { page, url } = payload;
        state.claimFilePaths = setIn(state.claimFilePaths, [fileId, page], {
          url,
          requestedDate: new Date().getTime(),
        }) as typeof state.claimFilePaths;
      }
    },
    'fetchClaimActivity/fulfilled': (state, action) => {
      const { dossierId, activityId } = action.meta.arg;
      state.claimActivities = setIn(
        state.claimActivities,
        [dossierId, activityId],
        action.payload
      ) as typeof state.claimActivities;
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
